import { ErrorBoundary, IconButton } from '@dixa/lib-shared-habla';
import produce from 'immer';
import type {
  InstrumentPanel as InstrumentPanelT,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import {
  AnalyticsMenu,
  AnalyticsMenuAction,
  type AnalyticsMenuActions,
  isTableInstrument,
} from 'lib-analytics-ui';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { createSearchUrl } from 'app/views/layout/views/search/utils';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';

import { type AnalyticsControlsProps } from '../../../controls/analyticsControls';

import { InstrumentComparisonModal } from './components/comparison/components/modal/instrumentComparisonModal';
import { AnalyticsPanelControlsModal } from './components/controls/analyticsPanelControls';
import { InstrumentModal } from './components/modal/instrumentModal';
import { InstrumentPanel } from './components/panel/instrumentPanel';
import { InstrumentRendererError } from './components/renderer/components/error/instrumentRendererError';
import {
  InstrumentRenderer,
  type InstrumentRendererProps,
} from './components/renderer/instrumentRenderer';
import { usePersistInstrumentSettings } from './utils/usePersistInstrumentSettings';

export type AnalyticsPanelProps = {
  /** The instrument panel to render. */
  panel: InstrumentPanelT;
  /**
   * Whether the panel is customizable. If true, it will enable dragging and
   * render a custom draggable handle in the panel header, as well as enable
   * editing the panel's description in the controls modal.
   */
  isCustomizable?: boolean;
  /** Whether the panel is in a custom view with mixed panels. */
  isMixedMode?: boolean;
  /** If provided, an action to pin the panel will be shown in the panel menu. */
  onPin?: (panel: InstrumentPanelT) => void;
  /** If provided, an action to unpin the panel will be shown in the panel menu. */
  onUnpin?: (panel: InstrumentPanelT) => void;
  /** Callback for when the panel's controls are changed. */
  onChangeControls?: (panel: InstrumentPanelT, applyToAll?: boolean) => void;
} & Pick<AnalyticsControlsProps, 'disabledControls'>;

export const AnalyticsPanel = memo(function AnalyticsPanel({
  panel,
  isCustomizable = false,
  isMixedMode = false,
  disabledControls,
  onPin,
  onUnpin,
  onChangeControls,
}: AnalyticsPanelProps): JSX.Element {
  const dispatch = useAppDispatch();
  const {
    dashboard,
    context,
    period,
    filters,
    settings,
    ui: { lastRefresh, openLinksInNewTab },
  } = useAnalyticsState(panel);

  const [showInDepthModal, setShowInDepthModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showControlsModal, setShowControlsModal] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  const [
    readInstrumentsQuery,
    {
      instruments,
      aiPrompts,
      requestId,
      isFetching,
      isError,
      fulfilledTimeStamp,
      originalArgs,
    },
  ] = analyticsApi.useLazyReadInstrumentsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      instruments: data?.data.instruments ?? [],
      aiPrompts: data?.data.ai_prompts ?? [],
      requestId: data?.meta.request_id,
    }),
  });

  const { instrumentSettings, setInstrumentSetting } =
    usePersistInstrumentSettings(panel);

  /** The relative time of the last time the instruments data was refreshed. */
  const lastRefreshTime = useShortHumanReadableTimer(fulfilledTimeStamp);

  /** Fetches the list of instruments for the panel. */
  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const response = await readInstrumentsQuery({
        id: panel.id,
        context,
        period,
        filters,
        settings: { ...settings, ...instrumentSettings },
        meta: {
          ...panel.controls?.meta,
          ...originalArgs?.meta,
          ...arg?.meta,
        },
        ...arg,
      });

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [
      context,
      filters,
      instrumentSettings,
      originalArgs?.meta,
      panel.controls?.meta,
      panel.id,
      period,
      readInstrumentsQuery,
      settings,
    ],
  );

  /**
   * Fetches the instrument data:
   *
   * - On initial component mount.
   * -
   * - When the `lastRefresh` timestamp changes. This timestamp is updated each
   *   time the user changes any of the global state controls (period, filters
   *   etc.), or when they manually click "Refresh data", or when the
   *   `forceRefresh` action is dispatched, e.g. by an auto-refresh timer.
   * -
   * - When the `instrumentSettings` change. This happens when the user changes a
   *   setting specific to an instrument, such as chart granularity.
   */
  useEffect(() => {
    readInstruments();

    // Only run this effect when the relevant data points that should trigger an API request change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRefresh, instrumentSettings]);

  /** Refetches the instrument data specifically when a panel's controls change. */
  useUpdateEffect(() => {
    if (!panel.controls) return;
    readInstruments(panel.controls);
  }, [panel.controls]);

  /** Handles opening the export instrument modal. */
  const handleExport = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'analytics:export',
          arg: {
            instruments,
            panel,
          },
        },
      }),
    );

    ampli.analyticsExportClicked({
      dashboard: dashboard.title,
      panel: panel.title,
    });
  }, [dashboard.title, dispatch, instruments, panel]);

  /** Handles opening the in-depth instrument modal. */
  const handleInDepth = useCallback(() => {
    setShowInDepthModal(true);

    ampli.analyticsInDepthClicked({
      dashboard: dashboard.title,
      panel: panel.title,
    });
  }, [dashboard.title, panel.title]);

  /** Handles opening the compare instrument modal. */
  const handleCompare = useCallback(() => {
    setShowCompareModal(true);

    ampli.analyticsCompareClicked({
      dashboard: dashboard.title,
      panel: panel.title,
    });
  }, [dashboard.title, panel.title]);

  /** Handles opening the panel controls modal. */
  const handleOpenControls = useCallback(() => {
    setShowControlsModal(true);

    ampli.analyticsPanelControlsOpened({
      dashboard: dashboard.title,
      panel: panel.title,
    });
  }, [dashboard.title, panel.title]);

  /** Handles refreshing the instrument data. */
  const handleRefresh = useCallback(() => {
    readInstruments();

    ampli.analyticsRefreshClicked({
      dashboard: dashboard.title,
      panel: panel.title,
    });
  }, [dashboard.title, panel.title, readInstruments]);

  /** Handles updating the panel's controls and refreshing the instrument data. */
  const handleChangeControls: NonNullable<typeof onChangeControls> =
    useCallback(
      (...args) => {
        setShowControlsModal(false);
        onChangeControls?.(...args);
      },
      [onChangeControls],
    );

  /**
   * Handles updating the instrument settings. If a panel has its own controls
   * from a quick view, the instrument settings are added onto the panel
   * controls. Otherwise they're persisted locally in `instrumentSettings`.
   */
  const handleSetInstrumentSetting: NonNullable<
    InstrumentRendererProps['onSetInstrumentSetting']
  > = useCallback(
    (key, value): void => {
      if (!panel.controls) {
        return setInstrumentSetting(key, value);
      }

      const updatedPanel = produce(panel, (draftPanel) => {
        Object.assign(draftPanel.controls!.settings, { [key]: value });
      });
      onChangeControls?.(updatedPanel);
    },
    [onChangeControls, panel, setInstrumentSetting],
  );

  /**
   * Handles pinning a panel to a quick view.
   *
   * The local instrument settings are added to the panel's controls, which
   * ensures that the panel is pinned with the current settings.
   */
  const handlePinPanel = useCallback(() => {
    const panelToPin: InstrumentPanelT = produce(panel, (draftPanel) => {
      (draftPanel.controls as Partial<typeof draftPanel.controls>) = {
        settings: {
          ...settings,
          ...instrumentSettings,
        },
      };
    });

    onPin?.(panelToPin);
  }, [instrumentSettings, onPin, panel, settings]);

  /**
   * If the instruments contain conversations, we include an action to view them
   * in Search.
   */
  const viewConversationsAction: AnalyticsMenuAction | null = useMemo(() => {
    const tableInstrument = instruments.find(isTableInstrument);
    if (!tableInstrument) return null;

    const ids = new Set<string>();
    const csidColumn = tableInstrument.data.columns.find(
      ({ format }) => format === 'csid',
    );

    if (csidColumn) {
      tableInstrument.data.rows.forEach(({ cells }) => {
        const cell = cells.find((cell) => cell.column === csidColumn.key);

        if (cell?.value) {
          ids.add(cell.value.toString());
        }
      });
    }

    if (!ids.size) return null;

    return {
      type: 'button',
      icon: 'search',
      label: 'View conversations',
      onClick: () => {
        ampli.analyticsViewConversationsClicked({
          dashboard: dashboard.title,
          panel: panel.title,
        });

        const path = createSearchUrl({
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'numeric_id',
                operator: 'is_one',
                values: Array.from(ids).map((id) => ({
                  label: `${id}`,
                  value: `${id}`,
                })),
              },
            ],
          },
          sortBy: {
            field: 'last_message_created_at',
            order: 'desc',
          },
        });

        const url = `${location.origin}${path}`;
        window.open(url, openLinksInNewTab ? '_blank' : '_self');
      },
    };
  }, [dashboard.title, instruments, openLinksInNewTab, panel.title]);

  /** The list of menu actions available for this instrument panel. */
  const actions: AnalyticsMenuActions = useMemo(() => {
    const conditionalActions: AnalyticsMenuActions = [];
    const externalActions: AnalyticsMenuActions = [];

    if (panel.exportable) {
      conditionalActions.push({
        type: 'button',
        icon: 'download',
        label: 'Export',
        onClick: handleExport,
      });
    }

    if (panel.comparable) {
      conditionalActions.push({
        type: 'button',
        icon: 'exchange',
        label: 'Compare',
        onClick: handleCompare,
      });
    }

    if (panel.has_in_depth && !panel.controls?.meta?.in_depth) {
      conditionalActions.push({
        type: 'button',
        icon: 'search-plus',
        label: 'Open in-depth data',
        onClick: handleInDepth,
      });
    }

    if (viewConversationsAction) {
      conditionalActions.push(viewConversationsAction);
    }

    if (onPin) {
      externalActions.push({
        type: 'button',
        icon: 'thumbtack',
        label: 'Pin to dashboard',
        onClick: handlePinPanel,
      });
    }

    if (onUnpin) {
      externalActions.push({
        type: 'button',
        icon: 'thumbtack-slash',
        label: 'Unpin from dashboard',
        onClick: () => onUnpin(panel),
      });
    }

    return [
      ...([
        ...conditionalActions,
        ...(conditionalActions.length > 0 ? [{ type: 'divider' }] : []),
      ] as AnalyticsMenuActions),

      ...([
        ...externalActions,
        ...(externalActions.length > 0 ? [{ type: 'divider' }] : []),
      ] as AnalyticsMenuActions),

      {
        type: 'button',
        icon: 'sync',
        label: 'Refresh data',
        description: lastRefreshTime
          ? `Last refreshed ${lastRefreshTime}`
          : undefined,
        disabled: isFetching,
        onClick: handleRefresh,
      },
    ];
  }, [
    handleCompare,
    handleExport,
    handleInDepth,
    handlePinPanel,
    handleRefresh,
    isFetching,
    lastRefreshTime,
    onPin,
    onUnpin,
    panel,
    viewConversationsAction,
  ]);

  /** The action elements to render in the panel header. */
  const actionElements: JSX.Element[] = useMemo(() => {
    const elements: JSX.Element[] = [
      <AnalyticsMenu
        key="menu"
        actions={actions}
        buttonProps={{
          'data-analytics-panel-menu': true,
        }}
      />,
    ];

    if (panel.controls) {
      elements.unshift(
        <IconButton
          key="controls"
          icon="preferences"
          variant="tertiary"
          onClick={handleOpenControls}
        />,
      );
    }

    return elements;
  }, [actions, handleOpenControls, panel.controls]);

  return (
    <>
      <ErrorBoundary
        fallback={
          <InstrumentPanel
            panel={panel}
            isDraggable={isCustomizable}
            actionElements={
              onUnpin && (
                <AnalyticsMenu
                  key="menu"
                  actions={[
                    {
                      type: 'button',
                      icon: 'thumbtack-slash',
                      label: 'Unpin from dashboard',
                      onClick: () => onUnpin(panel),
                    },
                  ]}
                />
              )
            }
          >
            <InstrumentRendererError />
          </InstrumentPanel>
        }
      >
        <InstrumentPanel
          ref={panelRef}
          panel={panel}
          actionElements={actionElements}
          isDraggable={isCustomizable}
        >
          <InstrumentRenderer
            panel={panel}
            instruments={instruments}
            aiPrompts={aiPrompts}
            requestId={requestId}
            isFetching={isFetching}
            isError={isError}
            onReadInstruments={readInstruments}
            onSetInstrumentSetting={handleSetInstrumentSetting}
          />
        </InstrumentPanel>
      </ErrorBoundary>

      {panel.has_in_depth && showInDepthModal && (
        <InstrumentModal
          panel={panel}
          onClose={() => setShowInDepthModal(false)}
          onPin={onPin}
        />
      )}

      {panel.comparable && showCompareModal && (
        <InstrumentComparisonModal
          panel={panel}
          panelRef={panelRef}
          onClose={() => setShowCompareModal(false)}
        />
      )}

      {panel.controls && showControlsModal && (
        <AnalyticsPanelControlsModal
          panel={panel}
          panelRef={panelRef}
          isEditableDescription={isCustomizable}
          disabledControls={disabledControls}
          onApply={handleChangeControls}
          {...(!isMixedMode && {
            onApplyAll: (panel) => handleChangeControls(panel, true),
          })}
          onClose={() => setShowControlsModal(false)}
        />
      )}
    </>
  );
});
